import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook personalizado para manejar listeners de Firebase con cleanup automático
 * @param {Function} setupListener - Función que configura el listener y retorna unsubscribe
 * @param {Array} dependencies - Dependencias para recrear el listener
 * @param {boolean} enabled - Si el listener está habilitado
 * @returns {Object} - Estado del listener y funciones de control
 */
export const useFirebaseListener = (setupListener, dependencies = [], enabled = true) => {
  const unsubscribeRef = useRef(null);
  const isActiveRef = useRef(false);
  
  const cleanup = useCallback(() => {
    if (unsubscribeRef.current && typeof unsubscribeRef.current === 'function') {
      console.log('🧹 Limpiando listener Firebase');
      unsubscribeRef.current();
      unsubscribeRef.current = null;
      isActiveRef.current = false;
    }
  }, []);
  
  const setup = useCallback(async () => {
    if (!enabled || isActiveRef.current) return;
    
    try {
      console.log('🔄 Configurando listener Firebase');
      const unsubscribe = await setupListener();
      
      if (typeof unsubscribe === 'function') {
        unsubscribeRef.current = unsubscribe;
        isActiveRef.current = true;
      } else {
        console.warn('⚠️ setupListener no retornó una función de cleanup');
      }
    } catch (error) {
      console.error('❌ Error configurando listener Firebase:', error);
    }
  }, [setupListener, enabled]);  useEffect(() => {
    if (enabled) {
      setup();
    } else {
      cleanup();
    }
    
    return cleanup;
    // ESLint disable because cleanup and setup are stable with useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(Array.isArray(dependencies) ? dependencies : []), enabled]);
  
  // Cleanup al desmontar
  useEffect(() => {
    return cleanup;
  }, [cleanup]);
  
  return {
    isActive: isActiveRef.current,
    cleanup,
    restart: () => {
      cleanup();
      setup();
    }
  };
};

/**
 * Hook para manejar múltiples listeners Firebase
 * @param {Array} listeners - Array de objetos {setupListener, dependencies, enabled, key}
 * @returns {Object} - Estado y funciones de control para todos los listeners
 */
export const useMultipleFirebaseListeners = (listeners = []) => {
  const listenersRef = useRef(new Map());
  
  const cleanupAll = useCallback(() => {
    console.log(`🧹 Limpiando ${listenersRef.current.size} listeners Firebase`);
    listenersRef.current.forEach((unsubscribe, key) => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    listenersRef.current.clear();
  }, []);
  
  const setupListener = useCallback((listener, key) => {
    if (!listener.enabled) return;
    
    try {
      console.log(`🔄 Configurando listener Firebase: ${key}`);
      const unsubscribe = listener.setupListener();
      
      if (typeof unsubscribe === 'function') {
        listenersRef.current.set(key, unsubscribe);
      }
    } catch (error) {
      console.error(`❌ Error configurando listener ${key}:`, error);
    }
  }, []);
  
  useEffect(() => {
    // Configurar todos los listeners
    listeners.forEach((listener) => {
      const key = listener.key || `listener_${Math.random()}`;
      
      // Limpiar listener existente si hay uno
      const existingUnsubscribe = listenersRef.current.get(key);
      if (existingUnsubscribe) {
        existingUnsubscribe();
      }
      
      // Configurar nuevo listener
      setupListener(listener, key);
    });
    
    return cleanupAll;
  }, [listeners, setupListener, cleanupAll]);
  
  return {
    cleanupAll,
    activeListeners: listenersRef.current.size,
    restartListener: (key) => {
      const listener = listeners.find(l => l.key === key);
      if (listener) {
        const existingUnsubscribe = listenersRef.current.get(key);
        if (existingUnsubscribe) {
          existingUnsubscribe();
        }
        setupListener(listener, key);
      }
    }
  };
};
